import type { BoardAppearance, ColumnLookExtra, Cover, Look } from '@/entities/appearance';
import type { MemberRole } from '@/entities/workspace';

export interface Board {
  id: string;
  workspace_id: string | null; // null for a guest invited to the board alone
  name: string;
  description: string | null;
  /** `#rrggbb`, or `null` = the default color derived from the id (`boardColor`). */
  color: string | null;
  /** Workspace-tile look and opened-board background; `null` = standard. */
  appearance: BoardAppearance | null;
  /** API path of the background image (`appearance.background.kind === 'image'`). */
  background_image_url: string | null;
  /** Where quick create puts cards; `null` = the first active column. */
  default_column_id: string | null;
  /** The single current owner (not the workspace owner unless they created / received it). */
  owner_user_id: string;
  /** The immutable creator. */
  created_by_user_id: string | null;
  /** "Created on behalf of" team; `null` for board-only guests and boards without one. */
  origin_team_id: string | null;
  visibility: 'private' | 'workspace' | 'public';
  version: number;
  is_favorite: boolean;
  created_at: string;
  updated_at: string;
  /** The section (UD-STRUCT) the board is filed in; `null` = the workspace root. */
  section_id?: string | null;
  /** Board labels in the board's own order (workspace board list only). */
  labels?: { id: string; name: string; color: string }[];
  /** Owner's display name; filled by the workspace board list only. */
  owner_name?: string | null;
  /** Workspace board list only: the viewer may reorder / restyle this board. */
  can_edit_details?: boolean;
  /** ISO weekdays (1 = Monday ... 7) the Calendar's Work week shows; default 1-5. */
  workweek_days: number[];
}

/** How a column's color is applied (a semantic style; the CSS lives in the UI). */
export type ColumnAppearance =
  'header' | 'border' | 'accent' | 'soft' | 'solid' | 'gradient' | 'pattern';
export const COLUMN_APPEARANCES: readonly ColumnAppearance[] = [
  'header',
  'border',
  'accent',
  'soft',
  'solid',
  'gradient',
  'pattern',
];

export interface Column {
  id: string;
  board_id: string;
  name: string;
  color: string | null;
  /** Irrelevant while `color` is `null` (gradient / pattern keep their own settings). */
  appearance_mode: ColumnAppearance;
  /** Gradient, pattern, advanced colors and background; `null` = none of them. */
  appearance: ColumnLookExtra | null;
  /** Default look of this column's cards (a card may override it). */
  card_look: Look | null;
  background_image_url: string | null;
  /** Markdown, `null` when empty. */
  description: string | null;
  position_key: string;
  /** `'calendar'` for the board's system Calendar column (hidden by default); `null` otherwise. */
  system_kind: 'calendar' | null;
}

/** Where a card is shown. A card is never tied to a column directly.
 * Pinning belongs to the placement (a future mirror may be pinned on one board only). */
export interface Placement {
  id: string;
  board_id: string;
  column_id: string;
  position_key: string;
  is_pinned: boolean;
}

export interface Label {
  id: string;
  board_id: string;
  name: string;
  color: string;
}

export interface ChecklistProgress {
  completed: number;
  total: number;
}

/** Derived by the server (never stored): see backend `cards/deadline.py`. */
export type DeadlineState = 'overdue' | 'today' | 'tomorrow' | 'later' | 'past';

/**
 * A deadline is a LOCAL END (`date` + optional `time`) in `timezone` (the user's profile
 * zone when it was set), with an optional period START (`start_date` + `start_time`).
 * The END is the due moment and decides overdue; no `time` means the end of that day.
 */
export interface Deadline {
  /** END date, `YYYY-MM-DD` */
  date: string;
  /** END time, `HH:MM:SS`; `null` for a date-only end. */
  time: string | null;
  timezone: string;
  /** The absolute moment the deadline expires (the END). */
  at: string;
  /** Optional period start (same timezone). */
  start_date: string | null;
  start_time: string | null;
  start_at: string | null;
  /** Events only: may become overdue after the end (tasks are always `false` here). */
  overdue_enabled: boolean;
  state: DeadlineState;
}

export interface ChecklistIndicator {
  id: string;
  title: string | null;
  completed: number;
  total: number;
}

/** Cached site metadata of a card whose whole title is one URL (the title stays the raw URL). */
export interface LinkPreview {
  /** `null` until the first fetch is queued. */
  id: string | null;
  url: string;
  hostname: string;
  status: 'pending' | 'ready' | 'failed';
  title: string | null;
  description: string | null;
  site_name: string | null;
  /** API path of the locally cached image. */
  image_url: string | null;
  fetched_at: string | null;
}

export interface Card {
  id: string;
  title: string;
  completed_at: string | null;
  version: number;
  created_at: string;
  updated_at: string;
  placement: Placement;
  /** In this card's own label order (= top-to-bottom order of the color strip segments). */
  label_ids: string[];
  /** `null` when the card has no checklist items. */
  checklist_progress: ChecklistProgress | null;
  /** Every checklist that has items, in content order (the tile shows the unfinished ones). */
  checklists: ChecklistIndicator[];
  deadline: Deadline | null;
  /** Server-derived: an active card (not done/archived/trashed) past its deadline. */
  is_overdue: boolean;
  cover: Cover | null;
  /** The card's own look; `null` = it follows its column's default card look. */
  appearance: Look | null;
  attachment_count: number;
  link_preview: LinkPreview | null;
  /** `unassigned` (nobody), `assigned` (executors) or `shared` (explicitly for everybody). */
  assignment_kind: 'unassigned' | 'assigned' | 'shared';
  assignee_ids: string[];
  /** When the card was archived (it is not on the board then); absent / `null` = active. */
  archived_at?: string | null;
}

/**
 * `GET /boards/{id}`. `columns` are in display order; `cards` are in display
 * order within each column (pinned first). The arrays' order is what the UI
 * renders, so optimistic moves only reorder arrays (the server assigns keys).
 */
export interface BoardDetail {
  board: Board;
  /** The caller's effective role on this board. */
  role: MemberRole;
  /** Effective permission identifiers (the server enforces them; the UI only reflects). */
  permissions: string[];
  columns: Column[];
  cards: Card[];
  labels: Label[];
  /** The caller's preference: show the system Calendar column in Kanban (default hidden). */
  show_calendar_column: boolean;
}

export type DeadlineFilter = 'has' | 'none' | 'overdue' | 'today' | 'tomorrow';
export type StatusFilter = 'open' | 'done';

export const DEADLINE_FILTERS: readonly DeadlineFilter[] = [
  'has',
  'none',
  'overdue',
  'today',
  'tomorrow',
];

export const STATUS_FILTERS: readonly StatusFilter[] = ['open', 'done'];

export type AssignmentFilter = 'me' | 'others' | 'shared' | 'unassigned';

export const ASSIGNMENT_FILTERS: readonly AssignmentFilter[] = [
  'me',
  'others',
  'shared',
  'unassigned',
];

/**
 * Board search/filter. Within one category the selected options are alternatives (OR); different
 * categories combine with AND; the text search narrows the result further. An EMPTY list is the
 * category's "All" (no restriction) - there is no separate "All" value, so it can never be
 * combined with specific options. Archived cards are never part of it.
 */
export interface CardFilter {
  q: string;
  labelIds: string[];
  /** `any`: a card with ANY selected label (default); `all`: with ALL of them. */
  labelMatch: 'any' | 'all';
  status: StatusFilter[];
  deadline: DeadlineFilter[];
  /** Who the task is for, from the viewer's point of view. */
  assignment: AssignmentFilter[];
}

export const EMPTY_FILTER: CardFilter = {
  q: '',
  labelIds: [],
  labelMatch: 'any',
  status: [],
  deadline: [],
  assignment: [],
};

/**
 * The canonical selection of one category: known values only, once each, in the category's own
 * order. Every producer (URL, UI) goes through it so equal selections are equal arrays.
 */
export function normalizeSelection<T extends string>(
  values: readonly string[],
  order: readonly T[],
): T[] {
  return order.filter((option) => values.includes(option));
}

/**
 * Toggles one option. With `collapseWhenFull`, ticking every option is the same as "All" (an
 * empty selection) - used where the options exactly partition the cards (open / done).
 */
export function toggleSelection<T extends string>(
  current: readonly T[],
  option: T,
  order: readonly T[],
  collapseWhenFull = false,
): T[] {
  const next = current.includes(option)
    ? current.filter((value) => value !== option)
    : [...current, option];
  const canonical = normalizeSelection(next, order);
  return collapseWhenFull && canonical.length === order.length ? [] : canonical;
}

export function isFilterActive(filter: CardFilter): boolean {
  return (
    filter.q.trim() !== '' ||
    filter.labelIds.length > 0 ||
    filter.status.length > 0 ||
    filter.deadline.length > 0 ||
    filter.assignment.length > 0
  );
}

/** One entry of the board trash: a column (with its cards) or a single card. */
/** A board in the workspace Trash. */
export interface DeletedBoard {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  trashed_at: string;
  /** Automatic permanent deletion time (7 days after deletion). */
  purge_after: string;
}

export interface TrashItem {
  operation_id: string;
  entity_type: 'column' | 'card';
  entity_id: string;
  title: string;
  card_count: number;
  trashed_at: string;
  /** Automatic permanent deletion time (7 days after deletion). */
  purge_after: string;
  /** `false` for a card whose column is itself in the trash. */
  restorable: boolean;
}
